import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ExternalLink } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { formatEuros } from "@/lib/format";
import { Barre, Bulle, Colonnes, Info, Ligne, Profil, Puces, Rond, Systeme, initialesDe } from "./Wa";

const depuis = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long" }) : "");

const Fil = ({ f, etape, suivante, onAvancer, onRetour }) => (
  <>
    <Barre titre={f.nom} sous={`${f.contact_nom || ""} · ${f.commune || ""}`} onRetour={onRetour} gauche={<Rond texte={initialesDe(f.nom)} couleur={etape?.temperature} />}
      droite={<Link to={`/fiches/${f.id}`} className="at-icon-btn" aria-label="Ouvrir la fiche" data-testid="wa-open-fiche"><ExternalLink size={18} /></Link>} testId="wa-pipeline-thread-bar" />
    <div className="wa-defil-col flex flex-col gap-2" data-testid="wa-pipeline-thread">
      <Systeme>Étape « {etape?.nom || f.etape} » depuis le {depuis(f.etape_entree_at)}</Systeme>
      <Bulle choisie heure={depuis(f.updated_at)} testId="wa-fiche-bubble">
        <p className="font-semibold">{f.operation} · {formatEuros(f.montant_estime)}</p>
        <p className="text-[13px] at-muted">{f.contact_nom} · {f.telephone} · {f.commune} ({f.code_postal})</p>
        <p className="text-[13px] mt-1">Source : {f.source}{f.champs?.financement ? ` · ${f.champs.financement}` : ""}</p>
      </Bulle>
      {f.prochaine_action && <Bulle heure={depuis(f.prochaine_action.echeance)}>⏰ Prochaine action : {f.prochaine_action.type_label}{f.prochaine_action.note ? ` · ${f.prochaine_action.note}` : ""} le {depuis(f.prochaine_action.echeance)}</Bulle>}
      {f.en_retard && <Systeme>⚠️ Délai de l'étape dépassé</Systeme>}
      <div className="ml-2 flex flex-wrap gap-2">
        {suivante && <button type="button" onClick={onAvancer} className="wa-avancer" data-testid="wa-advance-button">Avancer : {suivante.nom}<ArrowRight size={14} /></button>}
        <Link to={`/fiches/${f.id}`} className="wa-avancer" style={{ background: "var(--surface)", color: "var(--ink)" }} data-testid="wa-open-fiche-pill">Ouvrir la fiche</Link>
      </div>
    </div>
  </>
);

export const PipelineWa = () => {
  const [processus, setProcessus] = useState([]);
  const [pid, setPid] = useState(null);
  const [fiches, setFiches] = useState([]);
  const [filtre, setFiltre] = useState("tout");
  const [choix, setChoix] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  useEffect(() => { api.get("/processus").then((r) => { setProcessus(r.data); setPid(r.data[0]?.id); }); }, []);
  const charger = useCallback(() => { if (pid) api.get("/fiches", { params: { processus_id: pid } }).then((r) => setFiches(r.data)); }, [pid]);
  useEffect(() => { charger(); }, [charger]);
  const proc = processus.find((p) => p.id === pid);
  const etapes = useMemo(() => proc?.etapes || [], [proc]);
  const parCode = Object.fromEntries(etapes.map((e) => [e.code, e]));
  const visibles = fiches.filter((f) => filtre === "tout" || f.etape === filtre);
  const sel = visibles.find((f) => f.id === choix) || visibles[0];
  const actives = etapes.filter((e) => !e.sortie);
  const suivante = sel && actives[actives.findIndex((e) => e.code === sel.etape) + 1];
  const avancer = async () => {
    try { await api.post(`/fiches/${sel.id}/etape`, { etape: suivante.code }); toast.success("Étape mise à jour."); charger(); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  const compte = (code) => fiches.filter((f) => f.etape === code).length;
  const liste = (
    <>
      <Barre titre="Pipeline" sous={proc?.nom} testId="wa-pipeline-list-bar" />
      {processus.length > 1 && <Puces items={processus.map((p) => [p.id, p.nom])} valeur={pid} onChange={(v) => { setPid(v); setFiltre("tout"); }} testId="wa-pipeline-process" />}
      <Puces items={[["tout", `Tout ${fiches.length}`], ...etapes.map((e) => [e.code, `${e.nom} ${compte(e.code)}`])]} valeur={filtre} onChange={setFiltre} testId="wa-pipeline-steps" />
      <div className="overflow-y-auto flex-1 min-h-0 pb-24 md:pb-0" data-testid="wa-pipeline-list">
        {visibles.length === 0 && <p className="text-sm at-muted p-4">Aucune fiche à cette étape.</p>}
        {visibles.map((f) => (
          <Ligne key={f.id} actif={sel?.id === f.id} onClick={() => { setChoix(f.id); setOuvert(true); }} rond={<Rond texte={initialesDe(f.nom)} couleur={parCode[f.etape]?.temperature} />}
            titre={f.nom} sous={`${parCode[f.etape]?.nom || f.etape} · ${f.contact_nom || f.commune}`} heure={depuis(f.updated_at)} badge={f.en_retard ? "!" : null} alerte testId={`wa-pipeline-row-${f.id}`} />
        ))}
      </div>
    </>
  );
  const detail = sel && (
    <Profil initiales={initialesDe(sel.contact_nom || sel.nom)} nom={sel.contact_nom || sel.nom} sous={`${sel.nom} · ${sel.commune}`} testId="wa-pipeline-profile">
      <Info label="Téléphone" valeur={sel.telephone} />
      <Info label="Opération" valeur={sel.operation} />
      <Info label="Montant estimé" valeur={formatEuros(sel.montant_estime)} />
      <Info label="Étape" valeur={parCode[sel.etape]?.nom || sel.etape} />
      <Info label="Source" valeur={sel.source} />
    </Profil>
  );
  return (
    <div className="contents" data-testid="page-pipeline">
      <Colonnes ouvert={ouvert} liste={liste} fil={sel ? <Fil f={sel} etape={parCode[sel.etape]} suivante={suivante} onAvancer={avancer} onRetour={() => setOuvert(false)} /> : <Systeme>Aucune fiche.</Systeme>} detail={detail} testId="wa-pipeline" />
    </div>
  );
};
